import {
  ActionIcon,
  alpha,
  Anchor,
  Avatar,
  Badge,
  Checkbox,
  Group,
  HoverCard,
  Indicator,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useCallback, useMemo } from "react";
import {
  useLeadsContainerStore,
  useLeadsSheetStore,
} from "../../../../stores/LeadsStore";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  IconBell,
  IconMessage,
  IconPhone,
  IconSpeakerphone,
} from "@tabler/icons-react";
import {
  getCallColor,
  getDifference,
  getSelected,
  reminderTypes,
} from "../utils";
import {
  formatMinDate,
  parseDate,
  parseDateFuture,
} from "../../../../functions/dates";
import MoreActions from "./actions/MoreActions";
import PersonInCharge from "./responsable/PersonInCharge";
import { useLocation, useNavigate } from "react-router-dom";
import PositiveChanger from "./actions/PositiveChanger";
import ResumeWorkflow from "./actions/ResumeWorkflow";
import { IconLinkedin } from "../../../../components/Icons/IconLinkedin";
import { tagsColumn } from "../../../../layouts/Table/columns/tagsColumn";
import { updateProspect } from "../../../../api/PRMAPI";
import LeadHover from "./hoverContent/LeadHover";
import { IconEmail } from "../../../../assets/icons/IconEmail";

let dragGhostEl = null;
const createDragGhost = (selectedCount) => {
  const lang = useLanguage.getState().language;
  const ghost = document.createElement("div");
  ghost.className = "drag-ghost";

  ghost.innerHTML = `
    <div class="drag-ghost-main">${selectedCount} ${lang.database.contacts}</div>
  `;

  // put it off screen so user doesn't see the actual element
  ghost.style.position = "fixed";
  ghost.style.top = "-1000px";
  ghost.style.left = "-1000px";
  ghost.style.pointerEvents = "none";

  document.body.appendChild(ghost);
  return ghost;
};

const LeadItem = ({ current, container, index }) => {
  const lang = useLanguage((s) => s.language);
  const updateContainer = useLeadsContainerStore((s) => s.updateContainer);
  const updateContainerItem = useLeadsContainerStore(
    (s) => s.updateContainerItem,
  );
  const selectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const setSelectedIds = useLeadsContainerStore((s) => s.setSelectedIds);
  const selectedColumns = useLeadsContainerStore((s) => s.selectedColumns);
  const unselectedIds = useLeadsContainerStore((s) => s.unselectedIds);
  const setUnselectedIds = useLeadsContainerStore((s) => s.setUnselectedIds);

  const navigate = useNavigate();
  const location = useLocation();
  const handleShowDetail = (tab) => {
    const setOpened = useLeadsSheetStore.getState().setOpened;
    const setIndex = useLeadsSheetStore.getState().setIndex;
    const setCurrent = useLeadsSheetStore.getState().setCurrent;
    const setCurrentContainer =
      useLeadsSheetStore.getState().setCurrentContainer;
    const setActiveTab = useLeadsSheetStore.getState().setActiveTab;
    setOpened(true);
    setIndex(index);
    setCurrent(current);
    setCurrentContainer(container);
    if (tab) {
      setActiveTab(tab);
    }

    // Update URL so that on refresh, we reopen the lead sheet
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("contact", current.id);
    searchParams.set("container", container.id);
    searchParams.set("index", index);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  //#region About
  const about = useMemo(() => {
    const aboutList = [
      { key: "%last_name%", value: "lastName" },
      { key: "%first_name%", value: "firstName" },
      { key: "%job_title%", value: "jobTitle" },
      { key: "%company%", value: "company" },
      { key: "%phone%", value: "phone" },
      { key: "%contact_image_url%", value: "contact_image_url" },
    ];
    let result = {};

    aboutList.forEach((item) => {
      result[item.value] =
        current?.properties?.find((x) => x.identifier_placeholder === item.key)
          ?.value || (item.key === "%contact_image_url%" ? undefined : " - ");
    });

    return result;
  }, [current?.properties]);

  const difference = useMemo(() => {
    return getDifference(current?.calls[0]?.call_date);
  }, [current?.calls]);
  //#endregion

  //#region Selection
  const handleCheck = useCallback(
    (e) => {
      let newValue = e.target.checked;

      // Checking
      if (newValue === true) {
        if (selectedColumns.includes(container.id)) {
          let newSelection = { ...unselectedIds };
          newSelection[container.id] = newSelection[container.id]?.filter(
            (x) => x !== current.id,
          );
          setUnselectedIds(newSelection);
        } else {
          let newSelection = { ...selectedIds };
          newSelection[container.id] = [
            ...(newSelection[container.id] || []),
            current.id,
          ];
          setSelectedIds(newSelection);
        }
      }
      // Unchecking
      else {
        if (selectedColumns.includes(container.id)) {
          let newSelection = { ...unselectedIds };
          newSelection[container.id] = [
            ...(newSelection[container.id] || []),
            current.id,
          ];
          setUnselectedIds(newSelection);
        } else {
          let newSelection = { ...selectedIds };
          newSelection[container.id] = newSelection[container.id]?.filter(
            (x) => x !== current.id,
          );
          setSelectedIds(newSelection);
        }
      }
    },
    [
      selectedIds,
      unselectedIds,
      selectedColumns,
      setSelectedIds,
      setUnselectedIds,
      container.id,
      current.id,
    ],
  );

  const getChecked = useCallback(() => {
    const selected = Object.values(selectedIds).flatMap((x) => x);
    const notSelected = Object.values(unselectedIds).flatMap((x) => x);
    if (selectedColumns.includes(container.id)) {
      if (notSelected.includes(current.id)) {
        return false;
      }
      return true;
    }
    if (selected.includes(current.id)) {
      return true;
    }
    return false;
  }, [selectedIds, unselectedIds, selectedColumns, container.id, current.id]);
  //#endregion

  //#region Drag
  const onDragStart = (event) => {
    const selectedCount = getSelected();

    event.dataTransfer.setData(
      "application/prm",
      JSON.stringify({
        ...current,
        multiple: selectedCount > 1,
        selectedCount: selectedCount,
      }),
    );
    event.dataTransfer.effectAllowed = "move";
    if (selectedCount > 1) {
      dragGhostEl = createDragGhost(selectedCount);
      event.dataTransfer.setDragImage(dragGhostEl, 10, 20);
    }

    // setTimeout for chrome bug
    setTimeout(() => {
      document.querySelectorAll(".container-overlay").forEach((element) => {
        element.style.display = "block";
      });
    }, 10);
  };

  const onDragEnd = (event) => {
    setTimeout(() => {
      let dropped = document
        .getElementById("main-prm")
        .classList.contains("dropped");

      document.querySelectorAll(".container-overlay").forEach((element) => {
        element.style.display = "none";
      });
      document.querySelectorAll(".main-overlay").forEach((element) => {
        element.style.background = "none";
      });

      if (dropped) {
        document.getElementById("main-prm").classList.remove("dropped");

        const selectedCount = getSelected();
        if (selectedCount > 1) {
          return;
        } else {
          let newItems = container.items.filter(
            (item) => item.id !== current.id,
          );
          updateContainer(container.id, {
            items: newItems,
            total: container.total - 1,
            needCacheReset: true,
          });
        }
      }
    }, 50);
  };
  //#endregion

  const onTagEdited = (newTags) => {
    updateContainerItem(container.id, current.id, { tags: newTags });
  };

  return (
    <Paper
      withBorder
      radius={"md"}
      h={"calc(100% - 16px)"}
      style={{
        // boxShadow: getChecked()
        //   ? `${
        //       container?.color ? `#${container?.color}66` : "var(--mantine-color-primary-3)"
        //     } 0px 1px 3px`
        //   : `#00000009 1px 2px 3px`,
        opacity: current?.new ? 0.3 : 1,
        borderColor: getChecked()
          ? container?.color
            ? `#${container?.color}66`
            : "var(--mantine-color-primary-3)"
          : current?.new_reply
            ? current?.new_first_reply
              ? "var(--mantine-color-primary-6)"
              : "var(--mantine-color-secondary-6)"
            : "var(--mantine-color-primary-0)",
        borderWidth: current?.new_reply ? 3 : 1,
        cursor: "grab",
        position: "relative",
      }}
      bg={
        getChecked()
          ? container?.color
            ? `#${container?.color}12`
            : "var(--mantine-color-primary-0)"
          : "white"
      }
      px={"sm"}
      py={8}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      draggable
      component={Stack}
      gap={0}
      className="hover-source"
    >
      <Group gap={"sm"} wrap="nowrap" pr={18}>
        <Checkbox
          checked={getChecked()}
          onChange={handleCheck}
          size="xs"
          color={container?.color ? `#${container?.color}` : "primary"}
          radius={"sm"}
        />

        <HoverCard
          openDelay={1000}
          position="right"
          keepMounted={false}
          offset={2}
          withArrow
        >
          <HoverCard.Target>
            <Avatar
              color="initials"
              src={about.contact_image_url}
              name={about.firstName + " " + about.lastName}
              imageProps={{
                referrerPolicy: "no-referrer",
              }}
              radius={"xl"}
              size={32}
              variant="filled"
            />
          </HoverCard.Target>

          <HoverCard.Dropdown>
            <LeadHover container={container} current={current} />
          </HoverCard.Dropdown>
        </HoverCard>
        <Stack gap={4} flex={"1 1 auto"} w={0}>
          <Group wrap="nowrap">
            <Tooltip label={about.firstName + " " + about.lastName}>
              <Anchor
                truncate
                fw={600}
                c={"#333"}
                size="sm"
                onClick={() => handleShowDetail("summary")}
              >
                {about.firstName} {about.lastName}
              </Anchor>
            </Tooltip>
            {current?.last_reply_date && (
              <Tooltip label={parseDate(current.last_reply_date, false)}>
                <Badge
                  size="xs"
                  color={"secondary.1"}
                  bg={current?.new_reply ? undefined : "none"}
                  c={"black"}
                  fz={8}
                  pt={2}
                  miw={60}
                  ml={"auto"}
                >
                  {formatMinDate(current.last_reply_date)}
                </Badge>
              </Tooltip>
            )}
          </Group>

          <Tooltip label={about.jobTitle} disabled={about.jobTitle === " - "}>
            <Text size="xs" truncate="end">
              {about.jobTitle}
            </Text>
          </Tooltip>

          <Tooltip label={about.company} disabled={about.company === " - "}>
            <Text size="xs" truncate="end">
              {about.company}
            </Text>
          </Tooltip>
        </Stack>

        <MoreActions current={current} container={container} />
      </Group>

      {tagsColumn({
        editFunction: () => updateProspect,
        wrapperProps: { py: 4, px: 8, mt: 8 },
        onEdited: onTagEdited,
      }).renderCell({
        row: current,
        value: current.tags.map((e) => e.name).join(", "),
      })}

      <Group
        wrap="nowrap"
        mt={10}
        gap={"md"}
        w={"100%"}
        pt={8}
        style={{
          borderTop: "1px solid #00000018",
        }}
      >
        <Tooltip
          label={`${current?.programmations.length} ${lang.prm.campaign}${
            current?.programmations.length > 1 ? "s" : ""
          }`}
        >
          <Indicator
            color="red"
            inline
            offset={2}
            disabled={current?.programmations.length === 0}
            styles={{
              indicator: {
                width: 8,
                height: 8,
                minWidth: 0,
              },
            }}
            size={16}
          >
            <ActionIcon
              size={"xs"}
              variant="subtle"
              color="pink"
              className="lead-item-button"
              onClick={() => handleShowDetail("campaign")}
            >
              <IconSpeakerphone size={14} />
            </ActionIcon>
          </Indicator>
        </Tooltip>

        <Tooltip
          label={`${current?.calls.length} ${lang.prm.reminder}${
            current?.calls.length > 1 ? "s" : ""
          }`}
        >
          <Indicator
            color="red"
            inline
            offset={2}
            disabled={current?.calls.length === 0}
            styles={{
              indicator: {
                width: 8,
                height: 8,
                minWidth: 0,
              },
            }}
            size={16}
          >
            <ActionIcon
              size={"xs"}
              variant="subtle"
              color="red"
              className="lead-item-button"
              opacity={current?.calls.length === 0 ? undefined : 1}
              onClick={() => handleShowDetail("reminders")}
            >
              <IconBell size={14} />
            </ActionIcon>
          </Indicator>
        </Tooltip>

        <Tooltip
          label={`${current?.number_of_replies || " 0 "} ${
            lang.statistics.responses_viewer.answer
          }${(current?.number_of_replies || 0) > 1 ? "s" : ""}`}
        >
          <Indicator
            color="red"
            inline
            offset={2}
            disabled={current?.number_of_replies === 0}
            styles={{
              indicator: {
                width: 8,
                height: 8,
                minWidth: 0,
              },
            }}
            size={16}
          >
            <ActionIcon
              size={"xs"}
              variant="subtle"
              color="primary"
              className="lead-item-button"
              onClick={() => handleShowDetail("answers")}
            >
              {current?.number_of_replies === 0 ? (
                <IconMessage />
              ) : current.last_reply_type === "linkedin" ? (
                <IconLinkedin size={14} />
              ) : current.last_reply_type === "sms" ? (
                <IconMessage size={14} />
              ) : (
                <IconEmail size={14} />
              )}
            </ActionIcon>
          </Indicator>
        </Tooltip>

        {/* <Answers
              container={container}
              current={current}
              index={index}
              // disabled={noResponses.includes(container.id)}
              inPrm
            /> */}

        <PositiveChanger
          containerId={container.id}
          is_positive={current?.is_positive}
          itemId={current.id}
          pos="static"
        />

        <a href={`tel:${about.phone}`}>
          <ActionIcon
            size={"xs"}
            variant="subtle"
            color="primary"
            className="lead-item-button"
            disabled={about.phone === " - "}
          >
            <IconPhone size={14} />
          </ActionIcon>
        </a>

        {current.workflow_waiting_steps.length > 0 && (
          <ResumeWorkflow current={current} />
        )}

        {current?.new_reply && (
          <Badge
            size="xs"
            color={current?.new_first_reply ? "primary" : "secondary"}
            fz={10}
            h={18}
            w={40}
            pos={"absolute"}
            top={-10}
            left={2}
          >
            new{" "}
            {/* {current?.last_reply_date && (
              <>: {parseDate(current.last_reply_date)} </>
            )} */}
          </Badge>
        )}

        <PersonInCharge current={current} />
      </Group>

      {current.calls.length > 0 && (
        <Group mt={"auto"} pt={2}>
          <Tooltip label={current.calls[0]?.name}>
            <Text
              c={getCallColor(current.calls[0]?.call_date)}
              size="xs"
              fw={500}
            >
              {reminderTypes()[current.calls[0]?.type]}:{" "}
              {difference.result < 0
                ? parseDate(current.calls[0]?.call_date, false)
                : parseDateFuture(current.calls[0]?.call_date)}
            </Text>
          </Tooltip>
        </Group>
      )}
    </Paper>
  );
};

export default LeadItem;
